import Vue from 'vue'
import App from './App.vue'
import vuetify from './plugins/vuetify'
import Vuelidate from 'vuelidate'
import logo from './logo.js'
import DatetimePicker from 'vuetify-datetime-picker'
import axios from 'axios'
import store from './store'
import router from './router'
import conf from './conf'
import './registerServiceWorker'

Vue.use(DatetimePicker)
Vue.use(Vuelidate)
Vue.config.productionTip = false

console.log(logo)

// ─── Axios global defaults ────────────────────────────────────────────────────
axios.defaults.withCredentials = true

// Instancia sin interceptores — úsala para llamadas internas que no deben
// disparar el ciclo de refresco (logout, refresh en sí mismo).
export const rawAxios = axios.create({ withCredentials: true })

// ─── Helper: redirigir a login una sola vez ───────────────────────────────────
const AUTH_ROUTES = new Set(['login', 'logout'])
const UNLOGGED_ROUTES = new Set(['mfaSetup', 'maintenance'])

function redirectToLogin() {
    if (!AUTH_ROUTES.has(router.currentRoute.name)) {
        router.push({ name: 'logout' })
    }
}

// ─── Request interceptor ─────────────────────────────────────────────────────
axios.interceptors.request.use(
    config => {
        try {
            if (new URL(config.url).origin !== conf.host) {
                config.withCredentials = false
            }
        } catch (_) {
            // URL relativa → mismo origen, no tocar
        }
        return config
    },
    err => Promise.reject(err)
)

// ─── Response interceptor ────────────────────────────────────────────────────
let isRefreshing = false
let failedQueue = []

function processQueue(error) {
    failedQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve())
    failedQueue = []
}

axios.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 503) {
            router.push({ name: 'maintenance' })
            return Promise.reject(error)
        }

        const originalRequest = error.config

        if (error.response?.status !== 401) {
            return Promise.reject(error)
        }

        // 401 en un reintento → el refresh ya falló, salir
        if (originalRequest._retry) {
            redirectToLogin()
            return Promise.reject(error)
        }

        // Hay un refresh en curso → encolar y esperar
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject })
            })
                .then(() => rawAxios.request(originalRequest))
                .catch(err => Promise.reject(err))
        }

        originalRequest._retry = true
        isRefreshing = true

        return store.dispatch('login/refreshToken')
            .then(() => {
                processQueue(null)
                return rawAxios.request(originalRequest)
            })
            .catch(err => {
                processQueue(err)
                redirectToLogin()
                return Promise.reject(err)
            })
            .finally(() => {
                isRefreshing = false
            })
    }
)

// ─── Router guard ─────────────────────────────────────────────────────────────
router.beforeEach((to, from, next) => {
    const handle = () => {
        if (to.name === 'logout' || (!store.getters['login/loggedIn'] && to.name === 'login')) {
            return next()
        }

        if (!store.getters['login/loggedIn']) {
            return UNLOGGED_ROUTES.has(to.name) ? next() : next({ name: 'login' })
        }

        const accessAllowedRoles = to.meta.accessAllowedRoles
        const accessAllowedGroups = to.meta.accessAllowedGroups

        if (
            store.getters['login/hasPermission'](accessAllowedRoles) &&
            store.getters['login/ckeckGroupPermission'](accessAllowedGroups)
        ) {
            if (to.name === 'login' || !to.name) return next({ name: 'dashboard' })
            if (to.name === 'OfferConfigurator' && from.name === null) return next({ name: 'calculadora' })
            return next()
        }

        return next({ name: 'logout' })
    }

    if (!store.state.login.initialized && !UNLOGGED_ROUTES.has(to.name)) {
        store.dispatch('login/initialise').then(handle).catch(() => next({ name: 'login' }))
    } else {
        handle()
    }
})

new Vue({
    store,
    vuetify,
    validationMixin: Vuelidate.validationMixin,
    router,
    render: h => h(App),
}).$mount('#app')

// export the constants
export {
    AUTH_ROUTES,
    UNLOGGED_ROUTES,
}